import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { LibraryCollectionKpis, LibraryCollectionPreset, LibraryKpiKey, LibraryKpiWindow } from '@digital-twins/shared-types'

import { Skeleton } from '@/components/ui/skeleton'
import { formatKpiValue, toKpiDelta } from '@/pages/KnowledgeCollectionPage/collectionKpiFormat'
import { SocialMeasureSwitch, SocialStatDelta, formatTopicStat } from '@/pages/KnowledgeHubPage/social/SocialStatRow'

export interface CollectionKpiStripProps {
  /** Sets the four keys (`libraryKpiKeysForPreset`) and whether the window switch shows. */
  preset: LibraryCollectionPreset
  /** Null while loading: four hairline skeletons. */
  kpis: LibraryCollectionKpis | null
  window: LibraryKpiWindow
  onWindowChange: (window: LibraryKpiWindow) => void
  /** The collection's Topics: with none, the social callouts give way to one quiet line. */
  topicCount?: number
}

const WINDOW_OPTIONS: Array<LibraryKpiWindow> = ['30d', '90d', 'all']

/** Research and custom callouts are counts that ignore the window. */
const PRESETS_WITH_WINDOW: Array<LibraryCollectionPreset> = ['brand', 'competition', 'trend']

const WINDOW_DAYS: Record<LibraryKpiWindow, number | null> = { '30d': 30, '90d': 90, all: null }

/** Callouts read from social listening: they mean nothing without a Topic. */
const SOCIAL_KEYS: ReadonlyArray<LibraryKpiKey> = [
  'mentions',
  'brandNetSentiment',
  'shareOfVoice',
  'netSentimentGap',
  'postsInWindow',
]

/** The page section each callout jumps to (`id="collection-section-{…}"` on the section). */
const KPI_SECTION: Record<LibraryKpiKey, string> = {
  mentions: 'topics',
  brandNetSentiment: 'topics',
  shareOfVoice: 'topics',
  netSentimentGap: 'topics',
  postsInWindow: 'topics',
  studies: 'study',
  audiences: 'audience',
  twins: 'audience',
  datasets: 'dataset',
  documents: 'document',
}

/** The id of a collection page section, shared by the sections and the stat row's jump links. */
export function collectionSectionId(section: string): string {
  return `collection-section-${section}`
}

/** A zero count says nothing the sections do not: it never leads the row. */
export function isEmptyCountCallout(callout: { unit: string; value: number | null }): boolean {
  return callout.unit === 'count' && callout.value === 0
}

interface CollectionStatProps {
  id: LibraryKpiKey
  label: string
  value: string
  delta: ReactNode
}

/**
 * One stat in the display face, number over label. It jumps to its section
 * when that section is on the page; the section id comes from `KPI_SECTION`.
 */
function CollectionStat(props: CollectionStatProps) {
  const { id, label, value, delta } = props

  const onJump = () => {
    const target = document.getElementById(collectionSectionId(KPI_SECTION[id]))
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <button
      type="button"
      className="group flex min-w-0 flex-col items-start gap-1 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      onClick={onJump}
      data-testid={`collection-kpi-${id}`}
    >
      <span className="flex items-baseline gap-2 whitespace-nowrap">
        <span
          className="font-display text-[30px] font-normal leading-[1.1] tracking-[-0.01em] tabular-nums text-foreground @[760px]/collection:text-[34px]"
          data-testid={`collection-kpi-${id}-value`}
        >
          {value}
        </span>
        {delta}
      </span>
      <span className="text-[13px] text-muted-foreground underline-offset-4 transition-colors group-hover:text-foreground group-hover:underline">
        {label}
      </span>
    </button>
  )
}

/** A change worth showing: not flat, and over a previous base above zero (a rise from nothing has no rate). */
export function isVisibleKpiDelta(value: number | null, delta: { change: number; direction: string } | null): boolean {
  if (!delta || value == null || delta.direction === 'flat' || delta.change === 0) {
    return false
  }
  return value - delta.change > 0
}

/**
 * The collection's stat row, between the banner and the sections: up to
 * four display numbers over their labels (not a card), each a jump link to
 * its section, in `LIBRARY_KPI_KEYS` order with zero counts left out. The
 * change shows on `postsInWindow` only, with the "vs previous {n} days"
 * caption while a callout has one, and the window switch shows only while
 * a social callout (a number that depends on the window) is on screen.
 * Count labels follow their number ("1 Audience", "3 Audiences"). A change shows only when it is not flat and
 * its previous base is above zero. With no Topic, the social callouts and
 * the switch give way to a quiet "No posts yet". Stacks at 760 of
 * `@container/collection`.
 */
export function CollectionKpiStrip(props: CollectionKpiStripProps) {
  const { preset, kpis, window: windowValue, onWindowChange, topicCount = 1 } = props

  const { t, i18n } = useTranslation('app')

  const days = WINDOW_DAYS[windowValue]
  const hasTopics = topicCount > 0

  if (!kpis) {
    return (
      <div
        className="flex flex-wrap items-center gap-8 py-4 animate-in fade-in fill-mode-both delay-150"
        data-testid="collection-kpi-strip-loading"
        aria-hidden="true"
      >
        {[0, 1, 2, 3].map((index) => (
          <Skeleton key={index} className="h-14 w-28 rounded-lg border border-card-border bg-background" />
        ))}
      </div>
    )
  }

  const topicCallouts = kpis.callouts.filter((callout) => hasTopics || !SOCIAL_KEYS.includes(callout.key))
  const hasHiddenSocial = topicCallouts.length < kpis.callouts.length
  const callouts = topicCallouts.filter((callout) => !isEmptyCountCallout(callout))
  // The switch only shows when a number on screen depends on it: counts alone ignore the window.
  const hasWindowSwitch =
    PRESETS_WITH_WINDOW.includes(preset) && hasTopics && callouts.some((callout) => SOCIAL_KEYS.includes(callout.key))
  const deltas = callouts.map((callout) => {
    if (callout.key !== 'postsInWindow' || days == null) {
      return null
    }
    const delta = toKpiDelta(callout.value, callout.delta)
    return delta && isVisibleKpiDelta(callout.value, delta) ? delta : null
  })
  const hasDelta = deltas.some((delta) => delta != null)

  const deltaLabel = (index: number): string => {
    const delta = deltas[index]
    if (!delta) {
      return ''
    }
    const value = formatTopicStat(Math.abs(delta.change), i18n.language)
    return delta.direction === 'down'
      ? t('library.collections.kpis.deltaDown', { value, days })
      : t('library.collections.kpis.deltaUp', { value, days })
  }

  if (callouts.length === 0 && !hasHiddenSocial && !hasWindowSwitch) {
    return null
  }

  return (
    <section
      aria-label={t('library.collections.kpis.ariaLabel')}
      className="flex flex-wrap items-end gap-x-10 gap-y-5 pb-2 pt-4 @[760px]/collection:gap-x-14"
      data-testid="collection-kpi-strip"
    >
      {callouts.map((callout, index) => (
        <CollectionStat
          key={callout.key}
          id={callout.key}
          label={t(`library.collections.kpis.${callout.key}`, { count: callout.value ?? 0 })}
          value={
            callout.value == null
              ? t('library.collections.kpis.notAvailable')
              : formatKpiValue(callout.value, callout.unit, i18n.language)
          }
          delta={
            deltas[index] ? (
              <SocialStatDelta
                id={callout.key}
                delta={deltas[index]}
                label={deltaLabel(index)}
                testIdPrefix="collection-kpi"
              />
            ) : null
          }
        />
      ))}
      {hasHiddenSocial ? (
        <span className="pb-1 text-sm text-muted-foreground" data-testid="collection-kpi-no-posts">
          {t('library.collections.kpis.noPosts')}
        </span>
      ) : null}
      <div className="hidden flex-1 @[760px]/collection:block" />
      {hasDelta || hasWindowSwitch ? (
        <div className="flex w-full items-center justify-between gap-2 @[760px]/collection:w-auto @[760px]/collection:justify-end">
          {hasDelta ? (
            <span className="text-xs tabular-nums text-muted-foreground" data-testid="collection-kpi-change">
              {t('library.collections.kpis.change', { days })}
            </span>
          ) : null}
          {hasWindowSwitch ? (
            <SocialMeasureSwitch
              options={WINDOW_OPTIONS.map((option) => ({
                value: option,
                label: t(`library.collections.kpis.window.${option}`),
              }))}
              value={windowValue}
              onChange={onWindowChange}
              ariaLabel={t('library.collections.kpis.windowAria')}
              testIdPrefix="collection-kpi-window"
            />
          ) : null}
        </div>
      ) : null}
    </section>
  )
}
